import { KoobModal } from '@koob/ui';
import { KoobButton } from '@koob/ui';
import { Trans, useTranslation } from 'react-i18next';

export default function CompatibilityRecapModal({
  isOpen,
  onClose,
  values,
  onConfirmSaved,
  isSaved,
  isSaving,
  experienceName,
}) {
  const { t } = useTranslation('experiences');

  const cards = [
    {
      id: 1,
      title: t('compatibility.steps.1.title'),
      items: values.step1_best || [],
      isBest: true,
    },
    {
      id: 2,
      title: t('compatibility.steps.2.title'),
      items: values.step2_black || [],
      isBest: false,
    },
    {
      id: 3,
      title: t('compatibility.steps.3.title'),
      items: values.step3_best || [],
      isBest: true,
    },
    {
      id: 4,
      title: t('compatibility.steps.4.title'),
      items: values.step4_black || [],
      isBest: false,
    },
  ];

  return (
    <KoobModal open={isOpen} setOpen={onClose} widthClass="sm:max-w-3xl">
      <div className="flex flex-col max-h-[80vh] -mx-4 -mt-5 -mb-4 sm:-mx-6 sm:-mt-6 sm:-mb-6 overflow-hidden">
        <div className="p-5 border-b border-slate-100 flex justify-between items-center bg-white">
          <h2 className="text-sm font-bold text-slate-800">
            <i
              className={`fa-light mr-2 ${
                isSaved
                  ? 'fa-circle-check text-emerald-600'
                  : 'fa-list-check text-orange-500'
              }`}
            ></i>
            {isSaved
              ? t('compatibility.recap.title', 'Compatibility settings saved')
              : t(
                  'compatibility.recap.reviewTitle',
                  'Review compatibility settings',
                )}
          </h2>

          <i
            className="fa-light fa-xmark text-slate-400 hover:text-slate-600 cursor-pointer"
            onClick={onClose}
          ></i>
        </div>
        <span className="text-slate-400 px-8 py-2 block">
          <Trans
            i18nKey={
              isSaved
                ? 'experiences:compatibility:recap:description'
                : 'experiences:compatibility:recap:reviewDescription'
            }
            values={{ experienceName: experienceName }}
            components={{ bold: <strong /> }}
          />
        </span>
        <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-slate-50/50">
          {cards.map((card) => (
            <div
              key={card.id}
              className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm"
            >
              <div className="flex justify-between items-center mb-3">
                <div
                  className={`text-xs font-bold flex items-center gap-2 ${
                    card.isBest ? 'text-emerald-600' : 'text-red-600'
                  }`}
                >
                  <i
                    className={`fa-light ${
                      card.isBest ? 'fa-circle-check' : 'fa-circle-xmark'
                    }`}
                  ></i>
                  <span>
                    {card.id}. {card.title}
                  </span>
                </div>
                <span className="text-[11px] font-medium text-slate-400">
                  {t(
                    'compatibility.recap.experienceCount',
                    '{{count}} experience',
                    {
                      count: card.items.length,
                    },
                  )}
                </span>
              </div>

              <div className="space-y-2">
                {card.items.length === 0 ? (
                  <p className="text-xs text-slate-400 italic p-3 bg-slate-50/50 rounded-lg border border-dashed border-slate-200">
                    {t(
                      'compatibility.recap.empty',
                      'No experience added at this step.',
                    )}
                  </p>
                ) : (
                  card.items.map((exp) => (
                    <div
                      key={exp.id}
                      className={`p-3 rounded-lg border text-xs flex justify-between items-center ${
                        card.isBest
                          ? 'bg-emerald-50/20 border-emerald-100'
                          : 'bg-red-50/20 border-red-100'
                      }`}
                    >
                      <span className="font-semibold text-slate-700">
                        {exp.name}
                      </span>
                      <span className="text-[11px] text-slate-400">
                        {exp.startLocation?.title || exp.inCity?.title} →{' '}
                        {exp.endLocation?.title || exp.inCity?.title}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="p-4 border-t border-slate-100 flex justify-end gap-2 bg-white">
          <KoobButton size="sm" onClick={onClose}>
            {t('compatibility.recap.close')}
          </KoobButton>
          {!isSaved && (
            <KoobButton
              variant="primary"
              size="sm"
              onClick={onConfirmSaved}
              loading={isSaving}
            >
              {t('compatibility.recap.confirm')}
            </KoobButton>
          )}
        </div>
      </div>
    </KoobModal>
  );
}
